import md5 from "md5";
import { editProfileDataAPI } from "../api/userApi";
import { setUser } from "../redux/modules/userSlice";

export interface PlanData {
  id: string;
  name: string;
  tagline: string;
  taglineColor?: "pink" | "grey";
  description: string;
  price: string;
  period: string;
  buttonText: string;
  iconPath: string;
  isPopular?: boolean;
  popularBadgeText?: string;
  inheritedHeading?: string;
  includedFeatures: string[];
  creditsCount: number;
  creditsText: string;
  creditsValueText: string;
}

export const PLANS: PlanData[] = [
  {
    id: "onyx",
    name: "Onyx",
    tagline: "Take Control",
    taglineColor: "grey",
    description: "For members ready to go beyond basic discovery.",
    price: "$19.99",
    period: "/ month",
    buttonText: "Choose Onyx",
    iconPath: "/images/planicons/onyx.png",
    includedFeatures: [
      "Onyx Recognition Badge",
      "Advanced search",
      "Premium filters",
      "Unlimited messaging",
    ],
    creditsCount: 100,
    creditsText: "100 monthly credits",
    creditsValueText: "$10 standard credit value",
  },
  {
    id: "sapphire",
    name: "Sapphire",
    tagline: "Discover Smarter",
    taglineColor: "grey",
    description: "For members who want a faster, more intentional discovery experience.",
    price: "$49.99",
    period: "/ month",
    buttonText: "Choose Sapphire",
    iconPath: "/images/planicons/sapphire.png",
    inheritedHeading: "Inherited Onyx Benefits",
    includedFeatures: [
      "Sapphire Recognition Badge",
      "Enhanced recommendations",
      "Saved search",
    ],
    creditsCount: 300,
    creditsText: "300 monthly credits",
    creditsValueText: "$30 standard credit value",
  },
  {
    id: "diamond",
    name: "Diamond",
    tagline: "Unlock More",
    taglineColor: "pink",
    description: "For members who want smarter discovery and access to premium creator experiences.",
    price: "$99.99",
    period: "/ month",
    buttonText: "Choose Diamond",
    iconPath: "/images/planicons/diamond.png",
    isPopular: true,
    popularBadgeText: "RECOMMENDED",
    inheritedHeading: "Inherited Sapphire Benefits",
    includedFeatures: [
      "Diamond Recognition Badge",
      "Premium livestream access",
      "Personalized discovery",
      "Search alerts",
      "Premium creator experiences",
    ],
    creditsCount: 750,
    creditsText: "750 monthly credits",
    creditsValueText: "$75 standard credit value",
  },
  {
    id: "platinum",
    name: "Platinum VIP",
    tagline: "Experience The Highest Level",
    taglineColor: "grey",
    description: "For members seeking SugarCribs' highest level of recognition, access, and exclusivity.",
    price: "$249.99",
    period: "/ month",
    buttonText: "Choose Platinum VIP",
    iconPath: "/images/planicons/platinum.png",
    inheritedHeading: "Inherited Diamond Benefits",
    includedFeatures: [
      "Platinum VIP Recognition Badge",
      "Exclusive networking opportunities",
      "VIP events & experiences",
      "Priority Access to New Premium Features & Experiences",
    ],
    creditsCount: 2000,
    creditsText: "2,000 monthly credits",
    creditsValueText: "$200 standard credit value",
  },
];

export const CREDITS_MAP: Record<string, number> = {
  onyx: 100,
  sapphire: 300,
  diamond: 750,
  platinum: 2000,
};

export interface CCBillCheckoutOptions {
  returnUrl?: string;
  preMarkOnboarding?: boolean;
  dispatch?: any;
}

/**
 * Shared CCBill checkout redirect handler for membership subscriptions.
 * Prepares the MD5 digest and forwards user directly to CCBill FlexForms.
 */
export const proceedToCCBillSubscription = async (
  plan: PlanData,
  options?: CCBillCheckoutOptions
): Promise<string | void> => {
  try {
    // 1. User details from localStorage
    const stored = localStorage.getItem("user");
    const currentUser = stored ? JSON.parse(stored) : {};
    const userId = currentUser?._id || currentUser?.id || "";
    const userEmail = currentUser?.email || "";

    // 2. Pricing details
    const numericPrice = parseFloat(plan.price.replace(/[^0-9.]/g, "")).toFixed(2);
    const initialPeriod = 30;
    const recurringPeriod = 30;
    const numRebills = 99;
    const currencyCode = 840; // USD

    // 3. CCBill environment configuration
    const flexFormId =
      process.env.REACT_APP_CCBILL_FLEXFORM_ID ||
      "e66c35dc-1cf3-4944-a3a7-2ff889269957";
    const clientAccnum = process.env.REACT_APP_CCBILL_ACCOUNT || "954764";
    const clientSubacc =
      process.env.REACT_APP_CCBILL_SUB_ACCOUNT_RECURRING ||
      process.env.REACT_APP_CCBILL_SUB_ACCOUNT ||
      "0000";
    const salt = process.env.REACT_APP_CCBILL_SALT || "FEcuGgy2XUdZ7x7HeWPRQc6R";
    const baseUrl =
      process.env.REACT_APP_CCBILL_BASE_URL ||
      "https://api.ccbill.com/wap-frontflex/flexforms";

    // 4. Calculate MD5 recurring digest for CCBill
    // Strict order: initialPrice + initialPeriod + recurringPrice + recurringPeriod + numRebills + currencyCode + salt
    const formDigest = md5(
      `${numericPrice}${initialPeriod}${numericPrice}${recurringPeriod}${numRebills}${currencyCode}${salt}`
    ).toLowerCase();

    // 5. Pre-save onboarding completion state if requested or onboarding user
    if (options?.preMarkOnboarding) {
      try {
        await editProfileDataAPI({
          isOnboarding: true,
          validated: true,
          profileCompletion: true,
          phoneVerified: true,
        });
      } catch (err) {
        console.warn("Could not pre-mark onboarding complete on server:", err);
      }
    }

    const updatedUser = {
      ...currentUser,
      isOnboarding: true,
      validated: true,
      emailVerified: true,
      phoneVerified: true,
      profileCompletion: true,
      tier: plan.id,
    };
    localStorage.setItem("user", JSON.stringify(updatedUser));
    localStorage.setItem("hasRegisteredOrLoggedIn", "true");
    if (options?.dispatch) {
      options.dispatch(setUser(updatedUser));
    }

    // 6. Build redirect URL to CCBill FlexForms Dynamic Pricing
    // NOTE: CCBill F5 WAF rejects URLs containing 'localhost'. We fall back to production domain if local.
    const isLocalhost =
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1");
    const clientBase = isLocalhost
      ? process.env.REACT_APP_CLIENT_URL || "https://sugarcribs.com"
      : window.location.origin;
    const defaultReturnUrl = `${clientBase}/subscription-plans?payment=success&tier=${encodeURIComponent(
      plan.id
    )}`;
    const returnUrl = options?.returnUrl || defaultReturnUrl;

    const queryParams = [
      `clientAccnum=${encodeURIComponent(clientAccnum)}`,
      `clientSubacc=${encodeURIComponent(clientSubacc)}`,
      `initialPrice=${encodeURIComponent(numericPrice)}`,
      `initialPeriod=${encodeURIComponent(initialPeriod)}`,
      `recurringPrice=${encodeURIComponent(numericPrice)}`,
      `recurringPeriod=${encodeURIComponent(recurringPeriod)}`,
      `numRebills=${encodeURIComponent(numRebills)}`,
      `currencyCode=${encodeURIComponent(currencyCode)}`,
      `formDigest=${encodeURIComponent(formDigest)}`,
      `returnUrl=${encodeURIComponent(returnUrl)}`,
    ];

    if (userEmail) {
      queryParams.push(`email=${encodeURIComponent(userEmail)}`);
    }

    if (currentUser?.firstName) {
      queryParams.push(`customer_fname=${encodeURIComponent(currentUser.firstName)}`);
    }
    if (currentUser?.lastName) {
      queryParams.push(`customer_lname=${encodeURIComponent(currentUser.lastName)}`);
    }

    // Pass-through tracking variables for webhooks and post-checkout resolution
    if (userId) {
      queryParams.push(`userId=${encodeURIComponent(userId)}`);
      queryParams.push(`customField1=${encodeURIComponent(userId)}`);
    }
    queryParams.push(`context=subscription`);
    queryParams.push(`planId=${encodeURIComponent(plan.id)}`);
    queryParams.push(`tier=${encodeURIComponent(plan.id)}`);
    queryParams.push(`customField2=${encodeURIComponent(plan.id)}`);

    const paymentUrl = `${baseUrl}/${flexFormId}?${queryParams.join("&")}`;
    console.log(
      `[membershipCheckout] Redirecting to CCBill checkout for ${plan.name}:`,
      paymentUrl
    );
    window.location.href = paymentUrl;
    return paymentUrl;
  } catch (error) {
    console.error("Error initiating CCBill payment in proceedToCCBillSubscription:", error);
    throw error;
  }
};
